import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CouponDefinition, CouponCreateRequest, CouponUpdateRequest } from '@/lib/api-admin';
import { idempotencyKeyFor } from '@/lib/http-core';
import { adminFetch, apiClient } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { couponsQueryKey } from './useCouponsQuery';

export const useCouponCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation<CouponDefinition, Error, CouponCreateRequest>({
    mutationFn: async (body) => {
      const key = idempotencyKeyFor('POST', '/api/admin/v1/coupons', body);
      const [data] = await apiClient.request('post', '/api/admin/v1/coupons', {
        body,
        params: { header: { 'Idempotency-Key': key } },
      });
      return data as CouponDefinition;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};

export const useCouponDeleteMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (couponId) => {
      await apiClient.request('delete', '/api/admin/v1/coupons/{couponId}', {
        params: { path: { couponId } },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: couponsQueryKey({}) });
    },
  });
};

export interface CouponUpdateInput {
  couponId: string;
  body: CouponUpdateRequest;
}

export const useCouponUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation<CouponDefinition, Error, CouponUpdateInput>({
    mutationFn: async ({ couponId, body }) => {
      const key = idempotencyKeyFor('PUT', `/api/admin/v1/coupons/${couponId}`, body);
      const [data] = await apiClient.request('put', '/api/admin/v1/coupons/{couponId}', {
        body,
        params: {
          path: { couponId },
          header: { 'Idempotency-Key': key },
        },
      });
      return data as CouponDefinition;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};

/** QA-412 — 목록에서 사용/정지 단건 변경 (PATCH /{couponId}/active, SUPER_ADMIN·OPS_ADMIN). */
export const useCouponActiveMutation = () => {
  const qc = useQueryClient();
  return useMutation<CouponDefinition, Error, { couponId: string; active: boolean }>({
    mutationFn: async ({ couponId, active }) => {
      const res = await adminFetch(`/api/admin/v1/coupons/${couponId}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: CouponDefinition };
      return env.data as CouponDefinition;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};
